import Cookies from 'js-cookie';

/**
 * The visitor's Marketing Consent (see CONTEXT.md), as the cookie banner
 * stored it. This module is the only place the `cookie-consent` cookie is
 * read or written, so its value format can change in one place.
 *
 * Only call it from effects and event handlers: it reads `document.cookie`.
 */
export type MarketingConsentChoice = 'given' | 'declined';

const CONSENT_COOKIE_KEY = 'cookie-consent';

const STORED_VALUE: Record<MarketingConsentChoice, string> = {
  given: 'marketing',
  declined: 'necessary',
};

const CONSENT_COOKIE_EXPIRY_DAYS = 365;

/** Fired on `window` when the visitor gives Marketing Consent, so the Marketing Tags load without a reload. */
export const MARKETING_CONSENT_GIVEN_EVENT = 'cookie-consent-marketing-accepted';

/** Fired on `window` by Cookie Settings to reopen the cookie banner. */
export const OPEN_COOKIE_SETTINGS_EVENT = 'cookie-settings-open';

/** The stored choice, or `null` while the visitor has not chosen. */
export const readMarketingConsent = (): MarketingConsentChoice | null => {
  const stored = Cookies.get(CONSENT_COOKIE_KEY);
  if (stored === STORED_VALUE.given) return 'given';
  if (stored === STORED_VALUE.declined) return 'declined';
  return null;
};

/**
 * The choice in force on a page that has made its own: the stored one, or,
 * when nothing is stored, the page's own in a browser that keeps no cookies
 * (nothing could be stored), and none in one that does (the visitor cleared
 * their cookies, so the banner asks again).
 */
export const readMarketingConsentInForce = (onThisPage: MarketingConsentChoice | null): MarketingConsentChoice | null =>
  readMarketingConsent() ?? (navigator.cookieEnabled ? null : onThisPage);

export const saveMarketingConsent = (choice: MarketingConsentChoice) => {
  Cookies.set(CONSENT_COOKIE_KEY, STORED_VALUE[choice], {
    expires: CONSENT_COOKIE_EXPIRY_DAYS,
    path: '/',
    sameSite: 'lax',
  });
};

export const openCookieSettings = () => {
  window.dispatchEvent(new Event(OPEN_COOKIE_SETTINGS_EVENT));
};
